import { useTranslation } from 'react-i18next';
import { mdiBike, mdiRunFast, mdiSwim, mdiThermometer, mdiTerrain, mdiPulse, mdiCogOutline } from '@mdi/js';
import { workoutDisplayDigits as displayDigits, formatWorkoutNumber } from '../utils/workoutNumberFormat';
import { secToHMS } from '../utils/secToHMS';
import { fitMovementCount } from '../utils/fitMovementCount';
import type { RunningWorkout } from '../utils/runningWorkoutMetrics';
import { fitMetricGroup, fitMetricLabel, fitMetricUnit, visibleFitMetric, type FitRecordedMetadata, type FitRecordedMetric } from '../utils/fitRecordedMetrics';
import '../styles/fit-recorded-metrics.css';

const ICONS: Record<string, string> = {
  cycling: mdiBike, running: mdiRunFast, swimming: mdiSwim,
  physiology: mdiThermometer, terrain: mdiTerrain, other: mdiPulse, technical: mdiCogOutline,
};

export function FitAdditionalMetrics({ snapshot, excludeNames = [], workout, hideHeaders = false }: {
  snapshot?: FitRecordedMetadata | null; excludeNames?: Iterable<string>; workout?: RunningWorkout; hideHeaders?: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (!snapshot || snapshot.version !== 'fit_complete_v1') return null;
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const fa = locale.startsWith('fa');
  const excluded = new Set(excludeNames);
  const metrics = (snapshot.metrics ?? []).filter(metric => visibleFitMetric(metric) && !excluded.has(metric.name)).map(recorded => {
    const movement = fitMovementCount(recorded, workout);
    return { metric: movement?.metric ?? recorded, movement, group: movement?.group ?? fitMetricGroup(recorded) };
  });
  if (!metrics.length) return null;
  const format = (value: number | null, metric: FitRecordedMetric) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return metric.unit === 's' ? displayDigits(secToHMS(value), locale) : formatWorkoutNumber(value, locale, { maximumFractionDigits: 2 });
  };
  return <section className="fit-recorded fit-recorded--table ws-section" dir={i18n.dir()} aria-label={t('workoutInsights.additionalMetrics')}>
    {!hideHeaders && <div className="ws-section__head"><h4 className="ws-section__title">{t('workoutInsights.additionalMetrics')}</h4></div>}
    {metrics.length > 0 && <div className="ws-table">
      {!hideHeaders && <div className="ws-table__head ws-table__row ws-table__row--4"><span />{(['min', 'avg', 'max'] as const).map(part => <span key={part}>{t(`workoutDetail.summary.${part}`)}</span>)}</div>}
      {metrics.map(({ metric, movement, group }, index) => <div className={`ws-table__row ws-table__row--4${index < metrics.length - 1 ? ' ws-table__row--border' : ''}`} key={metric.key}>
        <span className="ws-table__label-col">
          <span className="ws-table__caption ws-table__label--icon" title={movement?.fromStrides ? t('fitMovementCounts.fromStrides') : undefined}><svg data-metric-group={group} width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={ICONS[group] ?? mdiPulse} /></svg><span>{displayDigits(movement ? t(`fitMovementCounts.${movement.kind}`) : fitMetricLabel(metric.name, fa), locale)}</span></span>
          <span className="ws-table__unit" dir="auto">{movement ? t(`fitMovementCounts.${movement.kind}Unit`) : metric.unit === 's' ? '' : fitMetricUnit(metric, fa)}</span>
        </span>
        {(['min', 'avg', 'max'] as const).map(part => <span className="ws-table__cell ltr-island" key={part}
          title={part === 'avg' ? t(metric.average_source === 'device' ? 'workoutInsights.deviceSummaryAverage' : 'workoutInsights.recordedSampleAverage') : undefined}
        >{format(metric[part], metric)}</span>)}
      </div>)}
    </div>}

  </section>;
}
